import React from 'react';

import autoBind from 'react-autobind';

import SubmissionDetailHotspot from "./SubmissionDetailHotspot.jsx"
import CompareAnswerDialog from "./CompareAnswerDialog.jsx"
import MarkdownRenderer from "../content/MarkdownRenderer.jsx"
import Localization from "../../Localization.jsx"
import { CircularProgress, Paper, Typography, Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Icon, FormControl, InputLabel, Select, MenuItem, LinearProgress, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

class SubmissionDetail extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();

    // state variables here
    this.state = {
      // aNumber : 1,
      // aString: "abc",
      // anArray: [],
      // anObject: {},
      submissionId: 0,
      hotspotList: [],
      currentAnswerData: null,
      currentComponentData: null,
      compareAnswerList: [],
      submissionSummary: null,
      isSummaryLoading: false,
      summaryDialogOpen: false,
      submissionListForUser: [],
      answersFailed: false,
      // The student's recorded GPS trail, parsed out of route_data in
      // getAnswersOK and handed to the map dialog.
      routeData: [],
    };
    autoBind(this);

    // This component is mounted ONCE by RetrieverPage (no `key`), so switching
    // students only calls refresh() again — two answer requests can be in
    // flight at the same time and resolve out of order. Every response carries
    // the token it was issued with and a late one is dropped, otherwise the
    // previous student's answers get painted under the current student's name.
    this.answersRequestId = 0;

    this.refresh();
  }

  refresh() {
    if (this.state.submissionId > 0) {
      const requestId = ++this.answersRequestId;
      this.setState({
        hotspotList: [],
        submissionSummary: null,
        isSummaryLoading: false,
        answersFailed: false,
        // Belongs to the OLD student — clear it or the map would open showing
        // the previous student's route.
        routeData: []
      });
      this.state.hotspotList = [];
      const ok = (res) => this.getAnswersOK(res, requestId);
      const failed = (err) => this.getAnswersFailed(err, requestId);
      // A share link is opened by someone with no account, so the normal
      // bySubmission route (bearer-gated, and keyed by submission_id) is not
      // available. The token-scoped public route returns the same answers for
      // exactly the submission the link was issued for.
      if (this.props.isSharedView && this.props.shareToken) {
        this.props.root.refs.API.request("get_shared_answers_by_token", {token: this.props.shareToken}, ok, failed);
      } else {
        this.props.root.refs.API.request("get_answers_by_submission_id", {submission_id: this.state.submissionId}, ok, failed);
      }
    }
  }

  /**
   * get_submission_summary is a SPEND-BEARING Azure OpenAI call: the route has
   * no cache and no idempotency, so every request re-runs a full completion and
   * writes a fresh ai_processed_item row. It therefore runs ONLY when the user
   * actually asks to see the summary, never on load, and never for a school
   * whose AI privilege is off (that school could not open the dialog anyway).
   */
  loadSubmissionSummary() {
    const submissionId = this.state.submissionId;
    if (!(submissionId > 0) || this.props.isSharedView) {
      return;
    }
    if (this.state.isSummaryLoading) {
      return;
    }
    // Already fetched for this submission — reuse it instead of re-billing.
    if (this.state.submissionSummary && this.summaryForSubmissionId === submissionId) {
      return;
    }
    this.setState({ isSummaryLoading: true });
    this.props.root.refs.API.request(
      "get_submission_summary",
      { submission_id: submissionId },
      (res) => this.getSubmissionSummaryOK(res, submissionId),
      this.getSubmissionSummaryFailed
    );
  }

  getSubmissionSummaryOK(res, submissionId) {
    // A summary that arrives after the user moved to another student belongs to
    // the old one; keep it out of the dialog.
    if (submissionId !== undefined && submissionId !== this.state.submissionId) {
      return;
    }
    if (res.body) {
      this.summaryForSubmissionId = submissionId;
      this.setState({
        submissionSummary: res.body,
        isSummaryLoading: false
      });
    } else {
      console.error("getSubmissionSummaryOK: Invalid response structure", res);
      this.setState({ isSummaryLoading: false });
    }
  }

  getSubmissionSummaryFailed(err) {
    console.error("get submission summary failed", err);
    this.setState({ isSummaryLoading: false });
  }

  getAnswersOK(res, requestId) {
      // Drop a response the user has already navigated away from (see refresh).
      if (requestId !== undefined && requestId !== this.answersRequestId) {
          return;
      }
      // Initialize variables outside to ensure they are accessible throughout the function
      let routeData = [];
      if (!res.body || !res.body['result']) {
          console.error("getAnswersOK: Invalid response structure", res);
          this.setState({ answersFailed: true });
          return;
      }

      try {
          routeData = JSON.parse(res.body['result']['route_data'] || '[]');
          console.log("Parsed route data:", routeData);
          this.setState({ routeData });
  
          if (routeData.length > 0 && !this.props.isSharedView) {
              if (this.props.parent.refs && this.props.parent.refs.submissionDetailMapDialog) {
                  this.props.parent.refs.submissionDetailMapDialog.setRouteData(routeData);
              }
          }
      } catch (e) {
          console.error("getAnswersOK: Error parsing route_data", e);
      }
  
      try {
          // Process hotspot list
          if (res.body['result']['hotspot_list'] !== undefined) {
              console.log("Setting hotspotList to ", res.body['result']['hotspot_list']);
              this.setState({ hotspotList: res.body['result']['hotspot_list'] });
          } else {
              console.log("getAnswersOK: hotspot_list is undefined");
          }
      } catch (e) {
          console.error("getAnswersOK: Error handling hotspot_list", e);
      }
  
      try {
          if (res.body['result']['submission_list'] !== undefined) {
              let submission_list = res.body['result']['submission_list'];
              if (!this.props.isSharedView &&
                  this.props.parent.retrieverBarRef &&
                  this.props.parent.retrieverBarRef.current) {
                  this.props.parent.retrieverBarRef.current.setState({
                      hasRouteData: (routeData.length > 0),
                      submissionList: submission_list,
                  });
              }

              this.setState({ submissionListForUser: submission_list });
          }
      } catch (e) {
          console.error("getAnswersOK: Error handling submission_list", e);
      }

      // The AI summary is NOT fetched here: it costs a full Azure OpenAI
      // completion per call, and opening a student used to bill one whether or
      // not anyone looked at it. loadSubmissionSummary() runs on demand from
      // the View Summary button instead.
  }

  getAnswersFailed(err, requestId) {
    if (requestId !== undefined && requestId !== this.answersRequestId) {
      return;
    }
    console.error("get answers failed", err);
    // Without this the panel just renders an empty worksheet, which reads as
    // "the student answered nothing" instead of "the answers failed to load"
    // (e.g. a 403 on another school's submission).
    this.setState({ answersFailed: true });
    if (this.props.root && this.props.root.addNotification) {
      this.props.root.addNotification(Localization.strings.LOAD_ANSWERS_FAILED, false);
    }
  }

  getAllAnswers(answerData, componentData) {
    this.setState({
      currentAnswerData: answerData,
      currentComponentData: componentData,
    });

    let requestObj = {};
    let submissionList = this.props.parent.state.submissionList;
    let submissionIDList = [];
    for (let i = 0; i < submissionList.length; i++) {
      submissionIDList.push(submissionList[i].id);
    }
    requestObj['submission_id_list'] = submissionIDList;
    this.props.root.refs.API.request("get_all_answers_in_submission_list", requestObj, this.getAllAnswersOK, this.getAllAnswersFailed);
  }

  getAllAnswersOK(res) {
    // On the OK path res.body.result may be null/undefined/non-object (see API.jsx contract),
    // so guard before reading submission_list or the Compare Answers dialog never opens.
    const result = res && res.body && res.body.result;
    const list = (result && Array.isArray(result.submission_list)) ? result.submission_list : [];
    this.setState({
      compareAnswerList: list,
    });
    this.state.compareAnswerList = list;

    if (this.refs.compareAnswerDialog) {
      this.refs.compareAnswerDialog.setState({
        open: true,
      })
    }
  }

  getAllAnswersFailed(err) {
    console.log("get all answers failed " + err);
  }

  handleOpenSummaryDialog = () => {
    this.setState({ summaryDialogOpen: true });
  };

  handleCloseSummaryDialog = () => {
    this.setState({ summaryDialogOpen: false });
  };

  // Old renderSubmissionSummary removed; logic moved to header button

  render() {
    return (
      <div style={{maxHeight:this.props.maxHeight, overflow:'auto'}}>
        <CompareAnswerDialog root={this.props.root} parent={this} ref="compareAnswerDialog"/>
        {(() => {
          // Render submission header card with student name, submission time dropdown and action buttons
          const currentSubmissionId = this.state.submissionId || this.props.parent.state.currentSubmissionId;
          const submissionFromParent = (this.props.parent.state.submissionList || []).find((s) => s.id === currentSubmissionId);
          const submissionFromUser = (this.state.submissionListForUser && this.state.submissionListForUser.length > 0)
              ? this.state.submissionListForUser.find((s) => s.id === currentSubmissionId)
              : null;
          const currentSubmission = submissionFromParent || submissionFromUser;

          if (!currentSubmission) {
            return null;
          }

          // Hide the header card on shared view
          if (this.props.isSharedView === true) {
            return null;
          }

          const canExport = this.props.root.getPrivilege('ANSWER_EXPORT').value === '1';
          const submissionsForUser = this.state.submissionListForUser || [];
          const selectedValue = currentSubmissionId || 0;

          const handleSubmissionSelectionChange = (e) => {
            const newId = e.target.value;
            const list = submissionsForUser;
            const idx = list.findIndex((x) => x.id === newId);
            const chosen = list[idx];
            if (idx >= 0 && chosen) {
              // Sync dropdown state on the old top bar if present
              if (this.props.parent.retrieverBarRef && this.props.parent.retrieverBarRef.current) {
                this.props.parent.retrieverBarRef.current.setState({ userOption: newId });
              }
              // Use parent handler to switch submission (updates id, username, loads detail)
              this.props.parent.handleSubmissionClicked(idx, chosen.id, chosen.user_name);
            }
          };

          const handleExport = () => {
            const file = this.props.parent.props.localeData['name'] + '_' + (this.props.parent.state.currentUsername || currentSubmission.user_name) + '_' + selectedValue + '.xlsx';
            this.props.parent.download_ans(file, selectedValue);
          };

          const handleShare = () => {
            this.props.parent.handleShareClick();
          };

          const handleRoute = () => {
            this.props.parent.handleRouteClicked();
          };

          const hasRouteData = Array.isArray(this.state.routeData) && this.state.routeData.length > 0;

          const handleViewSummary = () => {
            // Fetch on demand — this is the paid call, so it only runs when the
            // teacher actually opens the summary.
            this.loadSubmissionSummary();
            this.handleOpenSummaryDialog();
          };

          return (
            <div style={{ margin: '8px 0 16px', padding: '0 16px' }}>
              <Paper style={{ padding: '16px', borderRadius: '8px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                  <div style={{ minWidth: '240px' }}>
                    <Typography variant="caption" style={{ fontWeight: 'bold' }}>
                      {'學生名稱'}
                    </Typography>
                    <Typography variant="subtitle1" style={{ display: 'block' }}>
                      {this.props.parent.state.currentUsername || currentSubmission.user_name || ''}
                    </Typography>
                  </div>
                  <div style={{ minWidth: '260px' }}>
                    <FormControl size="small" style={{ minWidth: '240px' }}>
                      <InputLabel id="submission-time-select-label">{Localization.strings.SUBMISSION_TIME}</InputLabel>
                      <Select
                        labelId="submission-time-select-label"
                        value={selectedValue || ''}
                        onChange={handleSubmissionSelectionChange}
                        label={Localization.strings.SUBMISSION_TIME}
                      >
                        {submissionsForUser && submissionsForUser.length > 0 ? (
                          submissionsForUser.map((s) => {
                            const t = s.submission_time || (s.time ? s.time : '');
                            return (
                              <MenuItem key={s.id} value={s.id}>
                                {t.split(' ')[1] || t}
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem disabled>—</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </div>
                </div>

                {/* Score row below name and submission time */}
                {(() => {
                  const rawScore = (currentSubmission && (currentSubmission['score'] !== undefined && currentSubmission['score'] !== null)) ? currentSubmission['score'] : (currentSubmission && (currentSubmission['score_value'] !== undefined && currentSubmission['score_value'] !== null) ? currentSubmission['score_value'] : null);
                  const rawMax = (currentSubmission && (currentSubmission['score_max'] !== undefined && currentSubmission['score_max'] !== null)) ? currentSubmission['score_max'] : (currentSubmission && (currentSubmission['max_score'] !== undefined && currentSubmission['max_score'] !== null) ? currentSubmission['max_score'] : null);
                  const scoreNum = rawScore !== null && rawScore !== undefined && rawScore !== '' ? parseFloat(rawScore) : null;
                  const maxNum = rawMax !== null && rawMax !== undefined && rawMax !== '' ? parseFloat(rawMax) : null;
                  const percent = (scoreNum !== null && maxNum && maxNum > 0)
                    ? Math.max(0, Math.min(100, (scoreNum / maxNum) * 100))
                    : (currentSubmission && currentSubmission['percent'] !== undefined ? parseFloat(currentSubmission['percent']) : 0);
                  const hasMarks = (scoreNum !== null && scoreNum >= 0 && maxNum !== null && maxNum >= 0);

                  // This header reads its row off the PARENT's submissionList, where
                  // RetrieverPage.applyAiScoresToSubmissionList annotates score-sorted
                  // rows with an AI average. That average is a PERCENTAGE, so it is
                  // labelled as one — but real marks win when the LOCALE has any.
                  const rawAiAverage = currentSubmission && currentSubmission['ai_average_score'];
                  const aiPercentNum = (rawAiAverage !== undefined && rawAiAverage !== null && rawAiAverage !== '')
                    ? parseFloat(rawAiAverage)
                    : NaN;
                  const isAiPercent = !hasMarks && !isNaN(aiPercentNum);
                  // Nothing to draw: no marks, and no AI percentage to fall back on.
                  if (!hasMarks && !isAiPercent) return null;

                  const aiAccent = '#ad1179';
                  const barValue = isAiPercent
                    ? Math.max(0, Math.min(100, aiPercentNum))
                    : (isNaN(percent) ? 0 : percent);

                  return (
                    <Tooltip
                      arrow
                      placement="top-start"
                      title={isAiPercent
                        ? (Localization.strings.AI_PERCENT_TIP
                            || 'Average of this student\'s AI assessment percentages — a percentage, not marks out of a maximum')
                        : (Localization.strings.SCORE_MARKS_TIP
                            || 'Marks earned, out of the marks assigned to this LOCALE\'s questions')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', cursor: 'help' }}>
                        <Icon style={{ color: isAiPercent ? aiAccent : '#666' }}>
                          {isAiPercent ? 'smart_toy' : 'emoji_events'}
                        </Icon>
                        <Box style={{ flex: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={barValue}
                            sx={{
                              height: 8,
                              borderRadius: '9999px',
                              backgroundColor: '#888',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: isAiPercent ? aiAccent : '#4caf50',
                                borderRadius: '9999px'
                              }
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          style={{ minWidth: isAiPercent ? '96px' : '72px', textAlign: 'right', color: isAiPercent ? aiAccent : undefined }}
                        >
                          {isAiPercent
                            ? `${Localization.strings.AI_SCORE || 'AI Score'}: ${Math.round(aiPercentNum * 100) / 100}%`
                            : (scoreNum !== null && maxNum !== null ? `(${scoreNum}/${maxNum})` : '(–/–)')}
                        </Typography>
                      </div>
                    </Tooltip>
                  );
                })()}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '16px' }}>
                  <Tooltip title={canExport ? Localization.strings.ARCHIVE : Localization.strings.UPGRADE_TO_PREMIUM}>
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<Icon>archive</Icon>}
                        onClick={handleExport}
                        disabled={!canExport}
                      >
                        {Localization.strings.ARCHIVE}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    startIcon={<Icon>share</Icon>}
                    onClick={handleShare}
                  >
                    {Localization.strings.SHARE}
                  </Button>
                  {/* <Button
                    variant="outlined"
                    startIcon={<Icon>map</Icon>}
                    onClick={handleRoute}
                  >
                    User Route
                  </Button> */}
                  {(() => {
                    // Copy logic from old View Summary button
                    const aiEnabled = this.props.root.getPrivilege('AI_AUTO_ASSESSMENT').value === '1';
                    if (!aiEnabled) return null;

                    // The summary is no longer pre-fetched (it bills a full
                    // completion), so the button's enabled state comes from the
                    // submission itself: a summary can only be produced once
                    // the answers have been AI-assessed. When the flag is
                    // absent, stay enabled rather than dead-ending the user.
                    const assessed = currentSubmission['is_finished_assessment'];
                    const canSummarise = (assessed === undefined || assessed === null)
                      ? true
                      : (parseInt(assessed, 10) === 1);

                    if (this.state.isSummaryLoading) {
                      return (
                        <Button 
                          variant="contained" 
                          disabled
                          startIcon={<Icon>summarize</Icon>}
                        >
                          <CircularProgress size={16} style={{ marginRight: '8px', color: 'white' }} />
                          {Localization.strings.LOADING_SUMMARY}
                        </Button>
                      );
                    }

                    if (!canSummarise) {
                      return (
                        <Tooltip 
                          title={Localization.strings.PLEASE_PERFORM_AI_ASSESSMENT}
                          placement="right"
                        >
                          <span>
                            <Button 
                              variant="contained" 
                              disabled={true}
                              startIcon={<Icon>summarize</Icon>}
                              style={{ opacity: 0.7 }}
                            >
                              {Localization.strings.VIEW_SUMMARY || 'View Summary'}
                            </Button>
                          </span>
                        </Tooltip>
                      );
                    }

                    return (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Icon>summarize</Icon>}
                        onClick={handleViewSummary}
                      >
                        {Localization.strings.VIEW_SUMMARY || 'View Summary'}
                      </Button>
                    );
                  })()}

                  {/* The composer half of ex_flutterv1's Map > "View Route
                      History": where the student actually went. Opens the existing
                      SubmissionDetailMapDialog, which draws the recorded GPS
                      trail from route_data — the button for it was previously
                      commented out as "User Route".

                      Disabled when the submission carries no trail: an empty map
                      reads as "the student never moved" rather than "the device
                      recorded nothing". */}
                  <Tooltip
                    title={hasRouteData
                      ? (Localization.strings.VIEW_ROUTE_HISTORY_TIP || '')
                      : (Localization.strings.NO_ROUTE_DATA || 'This submission has no recorded movement')}
                  >
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<Icon>timeline</Icon>}
                        onClick={handleRoute}
                        disabled={!hasRouteData}
                      >
                        {Localization.strings.VIEW_ROUTE_HISTORY || 'View Route History'}
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </Paper>
            </div>
          );
        })()}
        {/* Summary dialog (moved from renderSubmissionSummary to align with new header button) */}
        <Dialog
          open={this.state.summaryDialogOpen}
          onClose={this.handleCloseSummaryDialog}
          fullWidth
          maxWidth="lg"
          aria-labelledby="summary-dialog-title"
          style={{ zIndex: 100001 }}
          PaperProps={{
            style: {
              maxHeight: '90vh',
              minHeight: '60vh',
              borderRadius: '8px',
              backgroundColor: '#fff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              width: '90%'
            }
          }}
        >
          <DialogTitle 
            id="summary-dialog-title" 
            style={{ 
              padding: '16px 24px', 
              backgroundColor: '#9c27b0', 
              color: 'white',
              borderBottom: '1px solid #7b1fa2', 
              position: 'relative',
              fontWeight: 'bold'
            }}
          >
            {/* DialogTitle already renders an <h2>; a nested <h6> (Typography's
                default element for variant="h6") is invalid inside it. */}
            <Typography variant="h6" component="span" style={{ fontWeight: 'bold', color: 'white', display: 'block' }}>
              {(Localization.strings.SUBMISSION_SUMMARY || "Submission Summary for {0}").replace('{0}', this.props.parent.state.currentUsername || '')}
            </Typography>
            <IconButton 
              edge="end" 
              onClick={this.handleCloseSummaryDialog} 
              aria-label="close"
              style={{ 
                position: 'absolute', 
                right: '12px', 
                top: '12px',
                color: 'white'
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent 
            dividers 
            style={{ 
              padding: '32px 48px', 
              backgroundColor: '#ffffff',
              position: 'relative',
              height: 'auto',
              maxHeight: 'calc(85vh - 68px)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div style={{ 
              flexGrow: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: '32px',
              paddingLeft: '8px',
              boxSizing: 'border-box',
              width: '100%'
            }}>
              {this.state.isSummaryLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '24px 0' }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">{Localization.strings.LOADING_SUMMARY}</Typography>
                </div>
              ) : (
                <MarkdownRenderer content={(this.state.submissionSummary && this.state.submissionSummary.result && this.state.submissionSummary.result.result) ? this.state.submissionSummary.result.result : ""} inDialog={true} />
              )}
            </div>
          </DialogContent>
        </Dialog>

        {this.state.answersFailed && (
          <Typography variant="body2" align="center" style={{ color: '#b00020', padding: '24px 16px' }}>
            {Localization.strings.LOAD_ANSWERS_FAILED}
          </Typography>
        )}
        {this.state.hotspotList.map( (row, i) => {
          return (
            <SubmissionDetailHotspot parent={this} root={this.props.root}
              hotspotData={row['hotspot_detail']}
              answerDataList={row['answer_list']}
              submission={(this.state.submissionListForUser || []).find((s) => s.id === (this.state.submissionId || this.props.parent.state.currentSubmissionId))}
              isSharedView={this.props.isSharedView}
              key={row['hotspot_detail']['id']}
            />
          );
        })}
      </div>
    )
  }
}

export default SubmissionDetail;
